import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import React, { useContext, useEffect, useRef } from "react";
import { FindReplaceContext } from "./plugins/FindReplacePlugin";

// Import CSS for search highlights
import "./search-highlight.css";

const FindReplaceModal = () => {
  const ctx = useContext(FindReplaceContext);
  const searchInputRef = useRef<HTMLInputElement>(null);

  if (!ctx) return null;

  const {
    searchTerm,
    replaceTerm,
    caseSensitive,
    wholeWord,
    setSearchTerm,
    setReplaceTerm,
    setCaseSensitive,
    setWholeWord,
    findMatches,
    findNext,
    findPrevious,
    replaceCurrent,
    replaceAll,
    clearHighlights,
    matches,
    currentIndex,
    modalOpen,
    setModalOpen,
    isSearching,
  } = ctx;

  // Focus search input when modal opens
  // useEffect(() => {
  //   if (modalOpen && searchInputRef.current) {
  //     setTimeout(() => {
  //       searchInputRef.current?.focus();
  //       searchInputRef.current?.select();
  //     }, 100);
  //   }
  // }, [modalOpen]);

  const handleClose = () => {
    setModalOpen(false);
    clearHighlights();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      if (event.shiftKey) {
        findPrevious();
      } else {
        findNext();
      }
      event.preventDefault();
    } else if (event.key === "Escape") {
      handleClose();
    }
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { minHeight: "300px" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SearchIcon />
          Find & Replace
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <TextField
          ref={searchInputRef}
          label="Find"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          margin="dense"
          variant="outlined"
          placeholder="Enter text to search..."
          InputProps={{
            endAdornment: isSearching ? <CircularProgress size={20} /> : null,
          }}
        />

        <TextField
          label="Replace with"
          value={replaceTerm}
          onChange={(e) => setReplaceTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          margin="dense"
          variant="outlined"
          placeholder="Enter replacement text..."
        />

        <Box sx={{ display: "flex", gap: 2, mt: 1, mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={caseSensitive}
                onChange={(e) => setCaseSensitive(e.target.checked)}
                size="small"
              />
            }
            label="Case sensitive"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={wholeWord}
                onChange={(e) => setWholeWord(e.target.checked)}
                size="small"
              />
            }
            label="Whole word"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {matches.length > 0 ? (
              <>
                {currentIndex + 1} of {matches.length} matches
                {searchTerm && (
                  <Box
                    component="span"
                    sx={{ color: "primary.main", fontWeight: "medium", ml: 1 }}
                  >
                    "{searchTerm}"
                  </Box>
                )}
              </>
            ) : searchTerm ? (
              <>
                No matches found
                <Box component="span" sx={{ color: "text.secondary", ml: 1 }}>
                  for "{searchTerm}"
                </Box>
              </>
            ) : (
              "Enter search term to find matches"
            )}
          </Typography>

          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              onClick={findPrevious}
              disabled={matches.length === 0}
              size="small"
              title="Previous match (Shift+Enter)"
            >
              <PrevIcon />
            </IconButton>
            <IconButton
              onClick={findNext}
              disabled={matches.length === 0}
              size="small"
              title="Next match (Enter)"
            >
              <NextIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={() => {
            findMatches();
          }}
          variant="outlined"
          disabled={!searchTerm.trim() || isSearching}
          startIcon={<SearchIcon />}
        >
          Find All
        </Button>

        <Button
          onClick={replaceCurrent}
          disabled={matches.length === 0 || currentIndex === -1}
          variant="outlined"
          color="warning"
        >
          Replace
        </Button>

        <Button
          onClick={replaceAll}
          disabled={matches.length === 0}
          variant="contained"
          color="warning"
        >
          Replace All ({matches.length})
        </Button>

        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FindReplaceModal;
